import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import BaseList, { baseListStyles } from '../../../components/List/BaseList'
import { useFormikContext } from 'formik'
import { Badge, Button, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { CalendarMonthRegular, GuestAdd24Regular, StatusRegular } from '@fluentui/react-icons'
import { Stack, Text } from '@fluentui/react'
import { DocketEntryDto, DocketProceedingDto } from './DocketProceedingForm'
import DocketEntryDialog from './DocketEntryDialog'
import { Alert } from '@fluentui/react-components/unstable'
import { DocketProceedingPanelProps } from './FormContainer'
import { formatShortDate } from '../../../helpers/dateHelper'

export enum DocketEntryAction {
    ADD = 'add',
    EDIT = 'edit',
}

type DocketEntryListProps = {
    index: number
    item: DocketEntryDto
    editEntry: (index: number) => void
}

const useStyles = makeStyles({
    createButton: {
        marginTop: tokens.spacingVerticalS,
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
    list: {
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
    previousDate: {
        color: tokens.colorNeutralForegroundDisabled,
    },
})

function DocketEntryListItem(props: DocketEntryListProps): ReactElement {
    const item = props.item

    return (
        <Stack tokens={{ childrenGap: 's1' }} styles={{ root: baseListStyles.itemCell }} onClick={() => props.editEntry(props.index)}>
            <Stack horizontal tokens={{ childrenGap: 's1' }}>
                <Text variant="mediumPlus" style={{ flexGrow: 1 }}>
                    {item.title}
                </Text>
                <Badge size="medium" icon={<CalendarMonthRegular />} appearance="tint">
                    {formatShortDate(item.date)}
                </Badge>
            </Stack>
            {item.status && (
                <Stack horizontal tokens={{ childrenGap: 's1' }}>
                    <div style={{ flexGrow: 1 }}>
                        <Badge size="medium" icon={<StatusRegular />} appearance="tint">
                            {item.status}
                        </Badge>
                    </div>
                </Stack>
            )}
            {item.supplement && (
                <Text variant="small" style={{ flexGrow: 1 }}>
                    Aanvulling: {item.supplement}
                </Text>
            )}
            {item.remark && (
                <Text variant="small" style={{ flexGrow: 1 }}>
                    Opmerking: {item.remark}
                </Text>
            )}
            {item.result && <Text style={{ flexGrow: 1 }}>Uitkomst: {item.result}</Text>}
        </Stack>
    )
}

export default function DocketEntryList(props: DocketProceedingPanelProps): ReactElement {
    const styles = useStyles()
    const { values, setFieldValue } = useFormikContext<DocketProceedingDto>()
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const [index, setIndex] = useState(undefined)
    const initialValues = useMemo<DocketEntryDto>(() => {
        return values.entries?.[index] || undefined
    }, [index, openDialog])
    const [action, setAction] = useState<DocketEntryAction>()

    const addNewEntry = () => {
        const entries = [
            {
                id: undefined,
                title: undefined,
                date: undefined,
                previousDate: undefined,
                assignedTo: undefined,
                status: undefined,
                description: undefined,
                result: undefined,
            },
            ...(values.entries ? values.entries : []),
        ]
        setFieldValue('entries', entries)
        setAction(DocketEntryAction.ADD)
        setIndex(0)
        setOpenDialog(true)
    }
    const editEntry = (index: number) => {
        setAction(DocketEntryAction.EDIT)
        setIndex(index)
        setOpenDialog(true)
    }

    return (
        <>
            <Button className={styles.createButton} appearance="primary" icon={<GuestAdd24Regular />} onClick={addNewEntry}>
                Handeling toevoegen
            </Button>
            {values.entries?.length > 0 && (
                <BaseList<DocketEntryDto> items={values.entries} className={styles.list}>
                    {(item: DocketEntryDto, index: number) => <DocketEntryListItem index={index} item={item} editEntry={editEntry} />}
                </BaseList>
            )}
            {(!values.entries || values.entries?.length === 0) && (
                <div className={props.styles.alert}>
                    <Alert intent="info">Er zijn nog geen handelingen aangemaakt.</Alert>
                </div>
            )}
            <DocketEntryDialog show={openDialog} setOpen={setOpenDialog} initialValues={initialValues} index={index} action={action} {...props} />
        </>
    )
}
